import React, { useEffect, useState } from 'react';
import { apiHeaders } from '../api';

const API_BASE = 'https://bot.nt55.ru';

interface ChatRow {
  chat_id: number;
  type: string;
  title: string | null;
  status: string;
  account_id?: string | null;
}

interface Template {
  id: number;
  title: string;
  text: string;
  sort_order: number;
}

const Settings: React.FC = () => {
  const [isActive, setIsActive] = useState(true);
  const [operatorOnline, setOperatorOnline] = useState(false);
  const [chats, setChats] = useState<ChatRow[]>([]);
  const [message, setMessage] = useState('');

  // Шаблоны
  const [templates, setTemplates] = useState<Template[]>([]);
  const [tplTitle, setTplTitle] = useState('');
  const [tplText, setTplText] = useState('');
  const [tplOrder, setTplOrder] = useState(0);
  const [editingTplId, setEditingTplId] = useState<number | null>(null);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/`, { headers: apiHeaders() });
      if (res.ok) {
        const data = await res.json();
        setIsActive(data.is_active);
        setOperatorOnline(data.operator_online || false);
      }
    } catch (e) { console.error(e); }
  };

  const loadChats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/livechat/chats`, { headers: apiHeaders() });
      if (res.ok) setChats(await res.json());
    } catch (e) { console.error(e); }
  };

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/livechat/templates`, { headers: apiHeaders() });
      if (res.ok) setTemplates(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    loadSettings();
    loadChats();
    loadTemplates();
  }, []);

  const handleToggleActive = async () => {
    if (!window.confirm(`Вы уверены, что хотите ${isActive ? 'выключить' : 'включить'} бота?`)) return;
    const res = await fetch(`${API_BASE}/api/settings/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...apiHeaders() },
      body: JSON.stringify({ is_active: !isActive }),
    });
    if (res.ok) {
      setIsActive(!isActive);
      setMessage('Статус бота обновлён');
    } else setMessage('Ошибка обновления');
  };

  const handleToggleOperator = async () => {
    const newValue = !operatorOnline;
    const res = await fetch(`${API_BASE}/api/settings/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...apiHeaders() },
      body: JSON.stringify({ operator_online: newValue }),
    });
    if (res.ok) {
      setOperatorOnline(newValue);
      setMessage('Статус оператора обновлён');
    } else setMessage('Ошибка обновления');
  };

  // --- Шаблоны CRUD ---
  const resetTplForm = () => {
    setTplTitle('');
    setTplText('');
    setTplOrder(0);
    setEditingTplId(null);
  };

  const handleSaveTemplate = async () => {
    if (!tplTitle.trim() || !tplText.trim()) {
      setMessage('Заполните название и текст шаблона');
      return;
    }
    const url = editingTplId
      ? `${API_BASE}/api/livechat/templates/${editingTplId}`
      : `${API_BASE}/api/livechat/templates`;
    const method = editingTplId ? 'PUT' : 'POST';
    const body = editingTplId
      ? { title: tplTitle, text: tplText, sort_order: tplOrder }
      : { title: tplTitle, text: tplText, sort_order: tplOrder };
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...apiHeaders() },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setMessage(editingTplId ? 'Шаблон обновлён' : 'Шаблон создан');
      resetTplForm();
      loadTemplates();
    } else setMessage('Ошибка сохранения шаблона');
  };

  const handleEditTemplate = (t: Template) => {
    setEditingTplId(t.id);
    setTplTitle(t.title);
    setTplText(t.text);
    setTplOrder(t.sort_order);
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!window.confirm('Удалить шаблон?')) return;
    const res = await fetch(`${API_BASE}/api/livechat/templates/${id}`, {
      method: 'DELETE',
      headers: apiHeaders(),
    });
    if (res.ok) {
      setMessage('Шаблон удалён');
      loadTemplates();
    } else setMessage('Ошибка удаления');
  };

  const totalChats = chats.length;
  const activeChats = chats.filter(c => c.status === 'active').length;

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: 32, fontFamily: '"Inter", "Roboto", "Segoe UI", sans-serif' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a', marginBottom: 24 }}>Настройки</h2>

        {message && (
          <div style={{ background: '#e6f7ff', border: '1px solid #91d5ff', padding: '8px 16px', borderRadius: 8, marginBottom: 24, color: '#096dd9', fontWeight: 500 }}>
            {message}
          </div>
        )}

        {/* Активность бота */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span>🤖 Активность бота</span>
            <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 12, background: isActive ? '#e6fffb' : '#fff2e8', color: isActive ? '#08979c' : '#d46b08', fontWeight: 500 }}>
              {isActive ? 'Активен' : 'Неактивен'}
            </span>
          </div>
          <p style={{ color: '#555', fontSize: 13, margin: '8px 0 16px' }}>
            {isActive ? 'Бот отвечает на сообщения и выполняет сценарии.' : 'Бот выключен. Пользователи не получат ответа.'}
          </p>
          <button onClick={handleToggleActive} style={buttonStyle}>
            {isActive ? '🔴 Выключить бота' : '🟢 Включить бота'}
          </button>
        </div>

        {/* Статус оператора */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span>👨‍💻 Статус оператора</span>
            <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 12, background: operatorOnline ? '#e6fffb' : '#fff2e8', color: operatorOnline ? '#08979c' : '#d46b08', fontWeight: 500 }}>
              {operatorOnline ? 'Онлайн' : 'Офлайн'}
            </span>
          </div>
          <p style={{ color: '#555', fontSize: 13, margin: '8px 0 16px' }}>
            {operatorOnline ? 'Пользователи увидят "Оператор онлайн. Напишите свой вопрос".' : 'Пользователи увидят "Нет доступных операторов, напишите свой вопрос и оставьте контакт для связи".'}
          </p>
          <button onClick={handleToggleOperator} style={buttonStyle}>
            {operatorOnline ? '🔴 Выключить (офлайн)' : '🟢 Включить (онлайн)'}
          </button>
        </div>

        {/* Шаблоны быстрых ответов */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span>📝 Шаблоны быстрых ответов</span>
            <span style={{ fontSize: 13, color: '#888' }}>{templates.length} шт.</span>
          </div>
          <p style={{ color: '#555', fontSize: 13, margin: '8px 0 16px' }}>
            Будут доступны в LiveChat по кнопке 📝.
          </p>

          <div style={{ background: '#fafafa', padding: 12, borderRadius: 8, marginBottom: 16 }}>
            <label style={labelStyle}>Название</label>
            <input value={tplTitle} onChange={e => setTplTitle(e.target.value)} style={{ ...inputStyle, marginBottom: 8 }} placeholder="Например: Приветствие" />

            <label style={labelStyle}>Текст</label>
            <textarea value={tplText} onChange={e => setTplText(e.target.value)} style={{ ...inputStyle, minHeight: 80, marginBottom: 8, fontFamily: 'inherit' }} placeholder="Здравствуйте! Чем могу помочь?" />

            <label style={labelStyle}>Порядок (меньше — выше)</label>
            <input type="number" value={tplOrder} onChange={e => setTplOrder(Number(e.target.value))} style={{ ...inputStyle, marginBottom: 12, width: 100 }} />

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSaveTemplate} style={{ ...buttonStyle, background: '#1677ff', color: 'white', border: 'none' }}>
                {editingTplId ? 'Сохранить изменения' : '➕ Добавить шаблон'}
              </button>
              {editingTplId && (
                <button onClick={resetTplForm} style={buttonStyle}>Отмена</button>
              )}
            </div>
          </div>

          {templates.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: 20 }}>Пока нет шаблонов</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={thStyle}>Название</th>
                  <th style={thStyle}>Текст</th>
                  <th style={thStyle}>Порядок</th>
                  <th style={thStyle}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}><strong>{t.title}</strong></td>
                    <td style={tdStyle}>{t.text.length > 80 ? t.text.slice(0, 80) + '…' : t.text}</td>
                    <td style={tdStyle}>{t.sort_order}</td>
                    <td style={tdStyle}>
                      <button onClick={() => handleEditTemplate(t)} style={{ ...smallBtn, marginRight: 4 }}>✏️</button>
                      <button onClick={() => handleDeleteTemplate(t.id)} style={{ ...smallBtn, color: '#cf1322', borderColor: '#ffa39e', background: '#fff1f0' }}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Пользователи */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span>👥 Пользователи (чаты)</span>
            <span style={{ fontSize: 13, color: '#888' }}>Всего: {totalChats} | Активных: {activeChats}</span>
          </div>
          <button onClick={loadChats} style={{ ...buttonStyle, marginBottom: 16 }}>🔄 Обновить список</button>
          {chats.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center', padding: 20 }}>Нет сохранённых чатов</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={thStyle}>Chat ID</th>
                  <th style={thStyle}>Тип</th>
                  <th style={thStyle}>Лицевой счет</th>
                  <th style={thStyle}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {chats.map(c => (
                  <tr key={c.chat_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>{c.chat_id}</td>
                    <td style={tdStyle}>{c.type === 'dialog' ? 'Диалог' : c.type}</td>
                    <td style={tdStyle}>{c.account_id || '-'}</td>
                    <td style={tdStyle}>
                      <span style={{
                        fontSize: 12, padding: '2px 8px', borderRadius: 12,
                        background: c.status === 'active' ? '#e6fffb' : '#fff2e8',
                        color: c.status === 'active' ? '#08979c' : '#d46b08',
                        fontWeight: 500,
                      }}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/* Стили */
const cardStyle: React.CSSProperties = {
  background: 'white', borderRadius: 12, padding: 24, marginBottom: 24,
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0',
};

const cardHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 8, fontSize: 16, fontWeight: 600,
};

const buttonStyle: React.CSSProperties = {
  padding: '8px 18px', borderRadius: 6, border: '1px solid #d9d9d9',
  background: '#f5f5f5', cursor: 'pointer', fontWeight: 500, fontSize: 13,
};

const smallBtn: React.CSSProperties = {
  padding: '4px 10px', borderRadius: 6, border: '1px solid #d9d9d9',
  background: '#f5f5f5', cursor: 'pointer', fontSize: 12,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, fontSize: 13, color: '#555',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 6,
  border: '1px solid #d9d9d9', fontSize: 14, boxSizing: 'border-box',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 12, color: '#666',
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px', fontSize: 13,
};

export default Settings;